import {
  createContext as createReactContext,
  useContext as useReactContext,
} from 'react';

export interface CreateStrictContextOptions {
  /** Throw an error if `useContext` is used outside the `Provider` */
  isStrict?: true;
  /** `hookName` is used to generate a more descriptive error message */
  hookName?: string;
  /** `providerName` is used to generate a more descriptive error message */
  providerName?: string;
  /** `errorMessage` is used to override the default error message */
  errorMessage?: string;
  /** `name` is used as a display name for the context */
  name?: string;
}

export interface CreateNoStrictContextOptions
  extends Omit<CreateStrictContextOptions, 'isStrict'> {
  isStrict: false;
}

export type CreateStrictContextReturn<T> = [
  React.Provider<T>,
  () => T,
  React.Context<T>,
];

export type CreateNoStrictContextReturn<T> = [
  React.Provider<T>,
  () => T | undefined,
  React.Context<T>,
];

function getErrorMessage(hook: string, provider: string) {
  return `${hook} returned \`undefined\`. Seems you forgot to wrap component within ${provider}`;
}

/** Creates a React context and a hook to consume it */
export function createContext<T>(
  options: CreateNoStrictContextOptions
): CreateNoStrictContextReturn<T>;
export function createContext<T>(
  options: CreateStrictContextOptions
): CreateStrictContextReturn<T>;
export function createContext<T>(
  options: CreateNoStrictContextOptions | CreateStrictContextOptions = {}
): CreateNoStrictContextReturn<T> | CreateStrictContextReturn<T> {
  const {
    name,
    isStrict = true,
    hookName = 'useContext',
    providerName = 'Provider',
    errorMessage,
  } = options;

  const Context = createReactContext<T | undefined>(undefined);

  Context.displayName = name;
  function useContext() {
    const context = useReactContext(Context);

    if (context === undefined && isStrict) {
      const error = new Error(
        errorMessage ?? getErrorMessage(hookName, providerName)
      );
      error.name = 'ContextError';
      throw error;
    }

    return context;
  }

  const hookReturn = [Context.Provider, useContext, Context];
  return hookReturn as CreateStrictContextReturn<T>;
}
